import React, { useEffect } from 'react';
import { Box, Heading, Text } from '@podiumhq/design-system';

import Notification from 'components/Notification';
import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useChangePage from 'hooks/useChangePage';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';
import { TRIAL_URL } from 'routes/constants';
import {
  TRIAL_ACCOUNT_STEP,
  TRIAL_ONBOARDING_STEP,
} from 'screens/Trials/constants';

import useSubmitCreateTrial from '../../hooks/useSubmitCreateTrial';
import useTrialPayment from '../../hooks/useTrialPayment';
import useValidateAccount from '../../hooks/useValidateAccount';
import { trialTitleStyles } from '../../styles';

import TrialPaymentButton from './TrialPaymentButton';
import TrialPaymentDisclaimer from './TrialPaymentDisclaimer';
import TrialPaymentForm from './TrialPaymentForm';

export const TrialPayment = () => {
  const { validateBusinessInfo, resetErrors } = useBusinessInfoContext() || {};
  const changePage = useChangePage();
  const { createNewTrial } = useSubmitCreateTrial();
  const trackEvent = useTrackAmplitudeEvent('trials.startTrial.visited');
  const { accountExists, isRearrangeStepsFlow } = useTrialsContext() || {};
  const { handlePaymentButtonSubmit, isSubmitting, setIsSubmitting } =
    useTrialPayment();
  const hasValidAccountFields = useValidateAccount();

  useEffect(() => {
    const isValid = hasValidAccountFields();
    if (accountExists || !isValid) {
      return changePage(`${TRIAL_URL}/${TRIAL_ACCOUNT_STEP}`, true);
    }

    // scroll to top (needed for mobile multi step view)
    window?.scrollTo(0, 0);

    // on page load to handle pressing back after cc form
    setIsSubmitting(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    const shouldCreateTrial =
      isRearrangeStepsFlow &&
      validateBusinessInfo({ trackEvent, displayErrors: false });

    handlePaymentButtonSubmit(event).then((data) => {
      if (data?.stripeSetupIntentId) {
        if (shouldCreateTrial) {
          return createNewTrial({ ...data }).then(() => {
            setIsSubmitting(false);
          });
        }

        resetErrors();
        changePage(`${TRIAL_URL}/${TRIAL_ONBOARDING_STEP}`);
      }
      setIsSubmitting(false);
    });
  };

  return (
    <Box>
      <Heading {...trialTitleStyles}>Start your free trial.</Heading>

      <Text fontSize="md" fontWeight="normal">
        Enter your card info, don't worry you won't be charged until your 14-day
        free trial ends.
      </Text>

      <Notification />

      <TrialPaymentForm />

      <TrialPaymentDisclaimer />

      <TrialPaymentButton
        handleSubmit={handleSubmit}
        isDisabled={!!isSubmitting}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
};

export default TrialPayment;
